import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ChevronRight, Loader2, UploadCloud, X, User, Home as HomeIcon,
  SprayCan, KeyRound, Camera, StickyNote, ClipboardCheck, PawPrint, ShieldCheck,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { QUOTE_SERVICES } from "@/data/services";
import { BUSINESS } from "@/data/business";
import { btnPrimary, container } from "@/lib/ui";
import { localBusinessSchema } from "@/lib/schema";

const STEPS = [
  { label: "Your Details", icon: User },
  { label: "Property", icon: HomeIcon },
  { label: "Services", icon: SprayCan },
  { label: "Access", icon: KeyRound },
  { label: "Photos", icon: Camera },
  { label: "Notes", icon: StickyNote },
  { label: "Review", icon: ClipboardCheck },
];

const PROPERTY_TYPES = ["House", "Apartment", "Townhouse", "Unit", "Airbnb"];
const CONDITIONS = ["Excellent", "Average", "Needs Deep Cleaning"];
const CONTACT_METHODS = ["Phone", "SMS", "Email", "WhatsApp"];
const BEDROOMS = ["Studio", "1", "2", "3", "4", "5+"];
const SMALL_NUMS = ["1", "2", "3", "4+"];
const YES_NO = ["Yes", "No"];
const PETS = ["Dog", "Cat", "Other"];
const MAX_PHOTOS = 20;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];

function preselectService(param) {
  if (!param) return [];
  const p = param.toLowerCase();
  const match = QUOTE_SERVICES.find(
    (s) => p.includes(s.toLowerCase()) || s.toLowerCase().includes(p.replace(" cleaning", "").trim())
  );
  return match ? [match] : [];
}

/* ---------- small presentational helpers ---------- */
function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-navy mb-2">
        {label} {required && <span className="text-brandgreen">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full h-12 rounded-xl border border-gray-200 bg-white px-4 text-[15px] text-inkdark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brandgreen focus:border-transparent transition";

function Pills({ options, value, onChange, name }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            data-testid={`opt-${name}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
              active
                ? "bg-brandgreen text-white border-brandgreen"
                : "bg-white text-navy border-gray-200 hover:border-brandgreen/50"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function MultiPills({ options, values, onToggle, name }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => {
        const active = values.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onToggle(o)}
            data-testid={`multi-${name}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold border transition-colors ${
              active
                ? "bg-navy text-white border-navy"
                : "bg-white text-navy border-gray-200 hover:border-navy/40"
            }`}
          >
            {active && <Check className="w-4 h-4" />}
            {o}
          </button>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-navy text-right">
        {Array.isArray(value) ? value.join(", ") : value}
      </span>
    </div>
  );
}

export default function QuotePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", preferred_contact: "Phone",
    property_type: "House", suburb: "", bedrooms: "", bathrooms: "", toilets: "",
    living_areas: "", storeys: "", garage: "", condition: "Average",
    services: [], customer_home: "Yes", key_available: "", lockbox: "",
    apartment_access: "", parking_available: "", lift_available: "", pets: [], notes: "",
  });

  useEffect(() => {
    const svc = params.get("service");
    const suburb = params.get("suburb");
    setForm((f) => ({
      ...f,
      services: svc ? preselectService(svc) : f.services,
      suburb: suburb || f.suburb,
    }));
  }, [params]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const toggleArr = (key, val) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.full_name.trim()) return "Please enter your full name.";
      if (!form.phone.trim()) return "Please enter your phone number.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email address.";
    }
    if (step === 2 && form.services.length === 0) return "Please select at least one cleaning service.";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`You can upload up to ${MAX_PHOTOS} images.`);
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (!ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: only image files are allowed.`);
        continue;
      }
      if (file.size > 12 * 1024 * 1024) {
        toast.error(`${file.name}: must be under 12MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/quotes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { file_id: res.data.file_id, name: file.name, url: URL.createObjectURL(file) }]);
      } catch (e) {
        toast.error(`${file.name}: upload failed.`);
      }
    }
    setUploading(false);
  }, [photos.length]);

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.file_id !== id));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, photo_ids: photos.map((p) => p.file_id) };
      await api.post("/quotes", payload);
      navigate("/quote/success");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Please review your details and try again.");
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="bg-neutralbg min-h-screen" data-testid="quote-page">
      <Seo
        title="Get a Free Quote — House Cleaning Melbourne"
        description="Request your free, no-obligation house cleaning quote in Melbourne. Quick multi-step form — tell us about your home and we'll prepare a personalised estimate."
        path="/quote"
        schema={[localBusinessSchema()]}
      />

      {/* Header */}
      <div className="bg-navy">
        <div className={`${container} py-10 lg:py-12`}>
          <span className="text-xs tracking-[0.22em] uppercase font-semibold text-brandgreen-light">Free Quote</span>
          <h1 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight">
            Request your free cleaning quote
          </h1>
          <p className="mt-3 text-white/70 max-w-2xl">
            It takes about a minute. The more detail you share, the more accurate your personalised estimate.
          </p>
        </div>
      </div>

      <div className={`${container} -mt-6 pb-24`}>
        <div className="rounded-3xl bg-white shadow-[0_16px_48px_rgba(15,61,94,0.10)] border border-gray-100 overflow-hidden">
          {/* Progress */}
          <div className="px-6 sm:px-10 pt-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-navy">
                Step {step + 1} of {STEPS.length} · {STEPS[step].label}
              </span>
              <span className="text-sm text-gray-400">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-brandgreen rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-5 hidden md:flex items-center justify-between">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i < step;
                const active = i === step;
                return (
                  <div key={s.label} className="flex flex-col items-center gap-1.5 flex-1">
                    <span className={`grid place-items-center w-9 h-9 rounded-full text-xs font-bold transition-colors ${
                      done ? "bg-brandgreen text-white" : active ? "bg-navy text-white" : "bg-gray-100 text-gray-400"
                    }`}>
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </span>
                    <span className={`text-[11px] font-medium ${active ? "text-navy" : "text-gray-400"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 sm:px-10 py-8 min-h-[340px]">
            {/* STEP 0 — Personal */}
            {step === 0 && (
              <div className="space-y-6 fade-up" data-testid="step-personal">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name" required>
                    <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. Jane Smith" data-testid="input-name" />
                  </Field>
                  <Field label="Phone Number" required>
                    <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 0400 000 000" data-testid="input-phone" />
                  </Field>
                </div>
                <Field label="Email Address" required>
                  <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="input-email" />
                </Field>
                <Field label="Preferred Contact Method">
                  <Pills name="contact" options={CONTACT_METHODS} value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} />
                </Field>
              </div>
            )}

            {/* STEP 1 — Property */}
            {step === 1 && (
              <div className="space-y-6 fade-up" data-testid="step-property">
                <Field label="Property Type">
                  <Pills name="ptype" options={PROPERTY_TYPES} value={form.property_type} onChange={(v) => set("property_type", v)} />
                </Field>
                <Field label="Suburb">
                  <input className={inputCls} value={form.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="e.g. West Melbourne" data-testid="input-suburb" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Bedrooms"><Pills name="beds" options={BEDROOMS} value={form.bedrooms} onChange={(v) => set("bedrooms", v)} /></Field>
                  <Field label="Bathrooms"><Pills name="baths" options={SMALL_NUMS} value={form.bathrooms} onChange={(v) => set("bathrooms", v)} /></Field>
                  <Field label="Toilets"><Pills name="toilets" options={SMALL_NUMS} value={form.toilets} onChange={(v) => set("toilets", v)} /></Field>
                  <Field label="Living Areas"><Pills name="living" options={SMALL_NUMS} value={form.living_areas} onChange={(v) => set("living_areas", v)} /></Field>
                  <Field label="Storeys"><Pills name="storeys" options={["1", "2", "3+"]} value={form.storeys} onChange={(v) => set("storeys", v)} /></Field>
                  <Field label="Garage"><Pills name="garage" options={YES_NO} value={form.garage} onChange={(v) => set("garage", v)} /></Field>
                </div>
                <Field label="Approximate Property Condition">
                  <Pills name="condition" options={CONDITIONS} value={form.condition} onChange={(v) => set("condition", v)} />
                </Field>
              </div>
            )}

            {/* STEP 2 — Services */}
            {step === 2 && (
              <div className="space-y-4 fade-up" data-testid="step-services">
                <p className="text-sm text-gray-600">Select all the cleaning services you're interested in.</p>
                <MultiPills name="service" options={QUOTE_SERVICES} values={form.services} onToggle={(v) => toggleArr("services", v)} />
              </div>
            )}

            {/* STEP 3 — Access */}
            {step === 3 && (
              <div className="space-y-6 fade-up" data-testid="step-access">
                <Field label="Will the customer be home?"><Pills name="home" options={YES_NO} value={form.customer_home} onChange={(v) => set("customer_home", v)} /></Field>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Key available?"><Pills name="key" options={YES_NO} value={form.key_available} onChange={(v) => set("key_available", v)} /></Field>
                  <Field label="Lockbox?"><Pills name="lockbox" options={YES_NO} value={form.lockbox} onChange={(v) => set("lockbox", v)} /></Field>
                  <Field label="Apartment access?"><Pills name="aptaccess" options={YES_NO} value={form.apartment_access} onChange={(v) => set("apartment_access", v)} /></Field>
                  <Field label="Parking available?"><Pills name="parking" options={YES_NO} value={form.parking_available} onChange={(v) => set("parking_available", v)} /></Field>
                  <Field label="Lift available?"><Pills name="lift" options={YES_NO} value={form.lift_available} onChange={(v) => set("lift_available", v)} /></Field>
                </div>
                <Field label="Any pets?">
                  <MultiPills name="pets" options={PETS} values={form.pets} onToggle={(v) => toggleArr("pets", v)} />
                </Field>
                {form.pets.includes("Dog") && (
                  <div className="flex items-center gap-2.5 rounded-xl bg-brandgreen/10 px-4 py-3 text-sm text-brandgreen-dark font-medium" data-testid="pet-reminder">
                    <PawPrint className="w-4 h-4" /> Please keep dogs secured during cleaning.
                  </div>
                )}
              </div>
            )}

            {/* STEP 4 — Photos */}
            {step === 4 && (
              <div className="space-y-5 fade-up" data-testid="step-photos">
                <p className="text-sm text-gray-600">
                  Photos help us prepare a more accurate quote (kitchen, bathroom, living room, bedrooms, or any
                  area needing attention). Optional — up to {MAX_PHOTOS} images.
                </p>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
                  onClick={() => fileRef.current?.click()}
                  className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
                    dragging ? "border-brandgreen bg-brandgreen/5" : "border-gray-200 hover:border-brandgreen/50 bg-neutralbg/50"
                  }`}
                  data-testid="photo-dropzone"
                >
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white shadow-sm mx-auto">
                    <UploadCloud className="w-7 h-7 text-brandgreen" />
                  </span>
                  <p className="mt-4 font-semibold text-navy">Drag &amp; drop images here</p>
                  <p className="text-sm text-gray-500">or click to browse — {photos.length}/{MAX_PHOTOS} uploaded</p>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="photo-input" />
                </div>
                {uploading && (
                  <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</p>
                )}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {photos.map((p) => (
                      <div key={p.file_id} className="relative group rounded-xl overflow-hidden aspect-square border border-gray-100">
                        <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removePhoto(p.file_id); }}
                          className="absolute top-1.5 right-1.5 grid place-items-center w-7 h-7 rounded-full bg-navy/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-photo-${p.file_id}`}
                          aria-label="Remove photo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 5 — Notes */}
            {step === 5 && (
              <div className="space-y-3 fade-up" data-testid="step-notes">
                <Field label="Additional Notes">
                  <textarea
                    rows={7}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-inkdark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brandgreen focus:border-transparent transition resize-none"
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Special requests, important information, parking, access, or areas needing extra attention…"
                    data-testid="input-notes"
                  />
                </Field>
              </div>
            )}

            {/* STEP 6 — Review */}
            {step === 6 && (
              <div className="space-y-5 fade-up" data-testid="step-review">
                <div className="rounded-2xl bg-neutralbg/70 p-5 border border-gray-100">
                  <h3 className="font-heading font-semibold text-navy mb-1">Your Details</h3>
                  <SummaryRow label="Name" value={form.full_name} />
                  <SummaryRow label="Phone" value={form.phone} />
                  <SummaryRow label="Email" value={form.email} />
                  <SummaryRow label="Preferred contact" value={form.preferred_contact} />
                </div>
                <div className="rounded-2xl bg-neutralbg/70 p-5 border border-gray-100">
                  <h3 className="font-heading font-semibold text-navy mb-1">Property</h3>
                  <SummaryRow label="Type" value={form.property_type} />
                  <SummaryRow label="Suburb" value={form.suburb} />
                  <SummaryRow label="Bedrooms" value={form.bedrooms} />
                  <SummaryRow label="Bathrooms" value={form.bathrooms} />
                  <SummaryRow label="Toilets" value={form.toilets} />
                  <SummaryRow label="Living areas" value={form.living_areas} />
                  <SummaryRow label="Storeys" value={form.storeys} />
                  <SummaryRow label="Garage" value={form.garage} />
                  <SummaryRow label="Condition" value={form.condition} />
                </div>
                <div className="rounded-2xl bg-neutralbg/70 p-5 border border-gray-100">
                  <h3 className="font-heading font-semibold text-navy mb-1">Services</h3>
                  <SummaryRow label="Requested" value={form.services} />
                </div>
                <div className="rounded-2xl bg-neutralbg/70 p-5 border border-gray-100">
                  <h3 className="font-heading font-semibold text-navy mb-1">Access</h3>
                  <SummaryRow label="Customer home" value={form.customer_home} />
                  <SummaryRow label="Key available" value={form.key_available} />
                  <SummaryRow label="Lockbox" value={form.lockbox} />
                  <SummaryRow label="Apartment access" value={form.apartment_access} />
                  <SummaryRow label="Parking" value={form.parking_available} />
                  <SummaryRow label="Lift" value={form.lift_available} />
                  <SummaryRow label="Pets" value={form.pets} />
                </div>
                <div className="rounded-2xl bg-neutralbg/70 p-5 border border-gray-100">
                  <h3 className="font-heading font-semibold text-navy mb-1">Photos & Notes</h3>
                  <SummaryRow label="Photos" value={photos.length ? `${photos.length} uploaded` : ""} />
                  <SummaryRow label="Notes" value={form.notes} />
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <ShieldCheck className="w-4 h-4 text-brandgreen" />
                  Your details are kept private and are only used to prepare your quote.
                </div>
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="px-6 sm:px-10 py-6 border-t border-gray-100 flex items-center justify-between gap-4 bg-white">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-3 font-semibold text-navy disabled:opacity-30 hover:bg-neutralbg transition-colors"
              data-testid="wizard-back"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} className={btnPrimary} data-testid="wizard-next">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} className={`${btnPrimary} !px-8`} data-testid="wizard-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>REQUEST MY FREE QUOTE <Check className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Prefer to talk? Call <a href={`tel:${BUSINESS.phoneRaw}`} className="font-semibold text-navy hover:text-brandgreen">{BUSINESS.phoneDisplay}</a>
        </p>
      </div>
    </div>
  );
}
